import { useState, useMemo } from "react"
import PropTypes from "prop-types"
import { Sidenav, SidenavItem, SidenavSection } from "@planningcenter/tapestry"
import { Link } from "@reach/router"
import { Button } from "@planningcenter/tapestry-react"

const flattenNavItems = navItems =>
  Object.values(navItems)
    .flat()
    .flatMap(item =>
      item.type === "header" ? [...(item.children || []), item.action].filter(Boolean) : item
    )

const findItemById = (items, id) => items.find(item => item.id === id)

const findCurrentItem = items => items.find(item => item.isCurrent)

const getItemLabel = item => item?.name || item?.text || null

const SideNav = ({ navItems }) => {
  const [selectedItemId, setSelectedItemId] = useState(null)

  const flattenedItems = useMemo(() => flattenNavItems(navItems), [navItems])

  const getSelectedItemLabel = () => {
    const targetItem = selectedItemId
      ? findItemById(flattenedItems, selectedItemId)
      : findCurrentItem(flattenedItems)

    return getItemLabel(targetItem)
  }

  const renderSidenavItem = item => {
    const itemContent = (
      <>
        <span>{item.text}</span>
        {item.subText?.toString().length && (
          <span className="suffix c-neutral-700 font-size-5 fw-400 pl-1 ta-r ws-nw">
            {item.subText}
          </span>
        )}
      </>
    )

    return (
      <SidenavItem
        key={item.key || item.text}
        selected={item.isCurrent || item.id === selectedItemId}
      >
        {item.onClick && (
          <button type="button" onClick={item.onClick}>
            {itemContent}
          </button>
        )}
        {item.linkTo && (
          <Link
            to={item.linkTo}
            getProps={({ isCurrent }) => {
              if (isCurrent) {
                setSelectedItemId(item.id)
              } else if (!isCurrent && selectedItemId === item.id) {
                setSelectedItemId(null)
              }
              return {}
            }}
          >
            {itemContent}
          </Link>
        )}
      </SidenavItem>
    )
  }

  const renderSectionAction = ({ linkTo, id, ...restProps }) => {
    return (
      <div slot="label-actions">
        <Button
          as={Link}
          to={linkTo}
          getProps={({ isCurrent }) => {
            if (isCurrent) {
              setSelectedItemId(id)
            } else if (!isCurrent && selectedItemId === id) {
              setSelectedItemId(null)
            }
            return {}
          }}
          {...restProps}
        />
      </div>
    )
  }

  const processNavItems = items => {
    const allItems = Object.values(items).flat()
    const headerItems = allItems
      .filter(item => item.type === "header")
      .map(item => ({
        ...item,
        children: item.children?.filter?.(child => child.text) || [item.children]
      }))
    const nonHeaderItems = allItems.filter(item => item.type !== "header")
    return [...nonHeaderItems, ...headerItems]
  }

  const processedNavItems = processNavItems(navItems)
  const topLevelNavItems = processedNavItems.filter(item => item.type !== "header")
  const navItemSections = processedNavItems.filter(item => item.type === "header")

  return (
    <aside className="page__sidebar">
      <Sidenav selectedItemLabel={getSelectedItemLabel()}>
        <SidenavSection>{topLevelNavItems.map(renderSidenavItem)}</SidenavSection>
        {navItemSections.map(section => (
          <SidenavSection key={section.id} label={section.text}>
            {section.action && renderSectionAction(section.action)}
            {section.children.filter(item => item.text).map(renderSidenavItem)}
          </SidenavSection>
        ))}
      </Sidenav>
    </aside>
  )
}

SideNav.propTypes = {
  navItems: PropTypes.shape({
    text: PropTypes.string,
    subText: PropTypes.string,
    type: PropTypes.string,
    linkTo: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.shape({})
  })
}

export default SideNav
